import { Controller } from "@hotwired/stimulus"

// Open a modal on arrival, as though its link had been clicked.
//
// The picker and its previews are layered ONTO a page: every caller fetches
// the modal frame from a page already on screen, and a direct visit to
// /meal-picker returns a bare <turbo-frame> with no layout at all.
//
// So a redirect that wants to land somebody in the picker — creating a food
// from it, and returning to the new food's amount step — sends them to the
// host page carrying the picker's URL, and this does what the click would
// have done.
export default class extends Controller {
  static values = { url: String }

  connect() {
    if (!this.urlValue) return

    // Once. A Turbo cache restore re-connects controllers, and re-opening a
    // modal somebody has closed is worse than not opening it at all.
    if (this.element.dataset.opened) return
    this.element.dataset.opened = "true"

    // AFTER THE PAGE HAS FINISHED ARRIVING, not during.
    //
    // Stimulus connects this while Turbo is still swapping the body — the
    // stack shows connect() running inside replaceBody's childList
    // mutation — so the click landed on the page being torn down, and the
    // <turbo-frame id="modal"> of the page being built was not in the DOM
    // yet. Turbo fetched the picker, found no frame to put it in, and threw
    // TurboFrameMissingError.
    //
    // WAIT FOR THE FRAME, rather than for an event that may already have
    // fired. readyState is no help — during a Turbo swap the document
    // loaded long ago and still says "complete" — and turbo:load has been
    // and gone by the time a cached page re-connects its controllers.
    //
    // The frame appearing in the DOM is the actual precondition, so that is
    // what is waited on. It is in the layout of every page that can host a
    // modal, so this resolves on the next frame in practice.
    //
    // Guarded by spec/views/frame_escapes_in_js_spec.rb, which reads this
    // file: a timing race cannot be seen from a request spec, but the
    // property is in the code.
    this.whenFrameExists().then((frame) => frame && this.open())
  }

  whenFrameExists(attempts = 20) {
    return new Promise((resolve) => {
      const look = (left) => {
        const frame = document.querySelector("turbo-frame#modal")
        if (frame || left === 0) return resolve(frame)

        requestAnimationFrame(() => look(left - 1))
      }

      look(attempts)
    })
  }

  open() {

    const link = document.createElement("a")
    link.href = this.urlValue
    link.dataset.turboFrame = "modal"
    link.hidden = true
    document.body.append(link)
    link.click()
    link.remove()

    // The parameter has done its job; leaving it in the bar means a reload
    // or a back-button return re-opens the modal over a page the person has
    // moved on from.
    const url = new URL(window.location.href)
    url.searchParams.delete("open_picker")
    window.history.replaceState({}, "", url)
  }
}
